import { firstValueFrom, timer, Observable, takeUntil } from 'rxjs';
import retryWithBackoff from '../operators/retryWithBackoff';

export interface Retry<T> {
  // Function that communicates if retry should happen.
  shouldRetry: (arg: T) => boolean;
  // Initial backoff interval in ms.
  initBackoffInterval: number;
  // Float value to apply to backoff interval to add jitter.
  jitterFactor: number;
  // Max backoff interval
  maxBackoffInterval: number;
  // Max duration of the entire retry sequence in ms.
  maxDuration: number;
  // Timeout in ms for individual operation.
  operationTimeout: number;
  // Max retry count.
  retryCount: number;
}

export function retryPromise<R, T>(
  exec: (timeout: number) => Promise<R>,
  retryConfig: Retry<T>
): Promise<R> {
  const obs: Observable<R> = new Observable(subscriber => {
    exec(retryConfig.operationTimeout)
      .then((res: R) => {
        subscriber.next(res);
        subscriber.complete();
      })
      .catch(e => {
        subscriber.error(e);
      });
  });
  return firstValueFrom(
    obs.pipe(
      retryWithBackoff(
        retryConfig.retryCount,
        retryConfig.shouldRetry as (err: Error) => boolean,
        retryConfig.initBackoffInterval,
        retryConfig.maxBackoffInterval
      ),
      takeUntil(timer(retryConfig.maxDuration))
    )
  );
}
